import { useState, useCallback } from "react";

export interface CacheNode {
  id: number;     
  key: string;
  value: string;
}

function LruEngine(initialCapacity: number) {
  const [capacity, setCapacity] = useState(initialCapacity);
  const [map, setMap] = useState<Map<string, CacheNode>>(new Map());
  const [list, setList] = useState<CacheNode[]>([]);
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const [highlightNodeId, setHighlightNodeId] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);

  const moveNodeToHead = useCallback((node: CacheNode) => {
    setList((prev) => {
      const filtered = prev.filter((n) => n.id !== node.id);
      return [node, ...filtered];
    });
  }, []);

  const evictTail = useCallback(() => {
    setList((prev) => {
      if (prev.length === 0) return prev;
      const tail = prev[prev.length - 1];
      setMap((old) => {
        const newMap = new Map(old);
        newMap.delete(tail.key);
        return newMap;
      });

      return prev.slice(0, prev.length - 1);
    });
  }, []);

  const get = useCallback(
    async (key: string) => {
      if (!map.has(key)) return null;
      const node = map.get(key)!;
      setHighlightKey(key);
      setHighlightNodeId(node.id);
      setAnimating(true);

      await new Promise((res) => setTimeout(res, 350));
      moveNodeToHead(node);
      setAnimating(false);
      setHighlightKey(null);
      setHighlightNodeId(null);

      return node.value;
    },
    [map, moveNodeToHead]
  );

  const put = useCallback(
    async (key: string, value: string) => {
      let newNode: CacheNode;

      if (map.has(key)) {
        newNode = { ...map.get(key)!, value };
        setHighlightKey(key);
        setHighlightNodeId(newNode.id);
        setAnimating(true);

        await new Promise((res) => setTimeout(res, 350));

        setMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(key, newNode);
          return newMap;
        });

        moveNodeToHead(newNode);
      } else {
        newNode = {
          id: Date.now(),
          key,
          value,
        };

        setHighlightKey(key);
        setHighlightNodeId(newNode.id);
        setAnimating(true);

        await new Promise((res) => setTimeout(res, 350));

        setMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(key, newNode);
          return newMap;
        });

        setList((prev) => [newNode, ...prev]);

        if (list.length + 1 > capacity) {
          await new Promise((res) => setTimeout(res, 300));
          evictTail();
        }
      }

      setAnimating(false);
      setHighlightKey(null);
      setHighlightNodeId(null);
    },
    [map, list, capacity, moveNodeToHead, evictTail]
  );

  const reset = useCallback(() => {
    setMap(new Map());
    setList([]);
    setHighlightKey(null);
    setHighlightNodeId(null);
    setAnimating(false);
  }, []);

  return {
    capacity,
    setCapacity,
    map,
    list,
    get,
    put,
    reset,
    highlightKey,
    highlightNodeId,
    animating,
  };
}

export default LruEngine;